import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { softseeInstallPageQuery, softseeInstallTableQuery, type SoftseeDoor } from '../api/devices'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import { postKitForm } from './softseeKitForm'
import { SoftseeHtmlTable } from './SoftseeHtmlTable'
import type { ScreenProps } from './screens'

/**
 * Fila Install Applications - installApplications.php: Kits | Custom Kits
 * si tabelul InstallApplicationsModel. Install scrie INS/KIT.
 */

const HEADS = ['Name', 'Version', 'Category', 'Action']

export function DeviceSoftseeInstall(props: ScreenProps) {
  return <InstallPane {...props} door="softsee" />
}

export function DeviceOpteemumInstall(props: ScreenProps) {
  return <InstallPane {...props} door="opteemum" />
}

/**
 * Lista de instalare cu sursa hotarata de cel care o deschide - fila Install
 * Packages, unde Kits / Custom Kits sunt butoanele ei, nu ale listei
 * (devicePackages.php: `initDetailsTable.data = {device, filterType: source}`
 * si un singur tabel, fara al doilea comutator).
 */
export function SoftseeInstallList({ id, door, source }: { id: number; door: SoftseeDoor; source: KitSource }) {
  return <InstallPane id={id} door={door} fixed={source} />
}

type KitSource = 'kits' | 'custom_kits'

function InstallPane({ id, door, fixed }: { id: number; door: SoftseeDoor; fixed?: KitSource }) {
  const query = useQueryClient()
  const page = useQuery(softseeInstallPageQuery(id, door))
  const [picked, setFilter] = useState<KitSource>('kits')
  const filter = fixed ?? picked
  const [search, setSearch] = useState('')
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)

  const table = useQuery({
    ...softseeInstallTableQuery(id, door, { start, length, search, filterType: filter }),
    placeholderData: keepPreviousData,
    enabled: !!page.data,
    refetchInterval: 20_000,
  })

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  return (
    <div className="device-softsee-install">
      <div className="row">
        <div className="col-md-12">
          <SoftseeHtmlTable
            id="deviceSoftseeInstall"
            tableClass="install-table"
            heads={HEADS}
            rows={table.data?.data ?? []}
            total={table.data?.recordsFiltered ?? 0}
            fetching={table.isFetching}
            error={table.isError ? (table.error as Error).message : null}
            search={search}
            start={start}
            length={length}
            onSearch={(q) => {
              setSearch(q)
              setStart(0)
            }}
            onStart={setStart}
            onLength={(n) => {
              setLength(n)
              setStart(0)
            }}
            filters={
              /* Kits | Custom Kits la stanga, cautarea la dreapta - pe acelasi rand
                 (`SoftseeToolbar`), nu unul deasupra celuilalt. Cand sursa vine
                 de afara, comutatorul e al celui care a deschis lista. */
              fixed ? undefined : (
                <div className="btn-group toggle-kits" data-toggle="buttons">
                  <label className={`btn btn-theme-inverse btn-transparent${filter === 'kits' ? ' active' : ''}`}>
                    <input
                      type="radio"
                      name="options"
                      id="option1"
                      autoComplete="off"
                      value="kits"
                      checked={filter === 'kits'}
                      onChange={() => {
                        setFilter('kits')
                        setStart(0)
                      }}
                    />
                    <i className="fas fa-cloud-download-alt"></i> Kits
                  </label>
                  <label className={`btn btn-theme-inverse btn-transparent${filter === 'custom_kits' ? ' active' : ''}`}>
                    <input
                      type="radio"
                      name="options"
                      id="option2"
                      autoComplete="off"
                      value="custom_kits"
                      checked={filter === 'custom_kits'}
                      onChange={() => {
                        setFilter('custom_kits')
                        setStart(0)
                      }}
                    />
                    <i className="fas fa-cogs"></i> Custom Kits
                  </label>
                </div>
              )
            }
            onFormSubmit={async (form) => {
              if (!confirm('Are you sure?')) return
              try {
                const message = await postKitForm(form, id, door)
                toast(message, 'Success', 'success')
                await query.invalidateQueries({ queryKey: ['device', id, `${door}-install`] })
              } catch {
                toast('Something went wrong.', 'Error', 'error')
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}
